import { useEffect, useState } from "react";
import { api } from "./api";
import { AuthContext, type AuthContextValue, type AuthUser } from "./auth-context";
import { USER_ROLES } from "@textformatter/shared";

const AUTH_USER_STORAGE_KEY = "auth:user";

function loadCachedUser(): AuthUser {
	try {
		const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as {
			id?: unknown;
			email?: unknown;
			role?: unknown;
			hasPasswordAuth?: unknown;
			hasGoogleAuth?: unknown;
		};
		if (
			typeof parsed?.id !== "string" ||
			typeof parsed?.email !== "string" ||
			(parsed.role !== USER_ROLES.USER && parsed.role !== USER_ROLES.ADMIN)
		) {
			return null;
		}
		return {
			id: parsed.id,
			email: parsed.email,
			role: parsed.role,
			hasPasswordAuth: parsed.hasPasswordAuth === true,
			hasGoogleAuth: parsed.hasGoogleAuth === true,
		};
	} catch {
		return null;
	}
}

function saveCachedUser(user: AuthUser) {
	try {
		if (!user) {
			window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
			return;
		}
		window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
	} catch {
		// Ignore storage failures (private mode/quota).
	}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const res = await api.me();
      setUser(res.user);
      saveCachedUser(res.user);
    } catch {
      setUser(null);
      saveCachedUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setUser(loadCachedUser());
    refresh();
  }, []);

  async function login(email: string, password: string) {
    await api.login(email, password);
    await refresh();
  }

  async function register(email: string, password: string) {
    await api.register(email, password);
  }

  async function logout() {
    await api.logout();
    setUser(null);
    saveCachedUser(null);
  }

  const value: AuthContextValue = { user, loading, login, register, logout, refresh };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
